import { apiClient } from './api';

function unwrapData<T>(res: { success: boolean; data?: T; message?: string; error?: string }, fallbackMsg: string): T {
  if (!res.success || res.data === undefined) {
    throw new Error(res.message || res.error || fallbackMsg);
  }
  return res.data;
}

export type ModerationStatus = 'pending' | 'approved' | '';

export type ReviewRow = {
  id: string;
  productId: string;
  productName: string;
  productSlug: string;
  author: string;
  rating: number;
  title: string;
  body: string;
  isVerifiedBuyer: boolean;
  isApproved: boolean;
  purchasedColors?: string[];
  purchasedVariants?: string[];
  userPhone?: string;
  createdAt: string;
};

export type QuestionRow = {
  id: string;
  productId: string;
  productName: string;
  productSlug: string;
  author: string;
  body: string;
  answersCount: number;
  pendingAnswers: number;
  isApproved: boolean;
  userPhone?: string;
  createdAt: string;
};

export type AnswerRow = {
  id: string;
  questionId: string;
  productId: string;
  productName: string;
  productSlug: string;
  questionAuthor: string;
  questionBody: string;
  author: string;
  body: string;
  isAdmin: boolean;
  isVerifiedBuyer: boolean;
  isAnonymous: boolean;
  isApproved: boolean;
  userPhone?: string;
  createdAt: string;
};

type PageResult<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

function mapReview(raw: Record<string, unknown>): ReviewRow {
  return {
    id: String(raw.id ?? ''),
    productId: String(raw.productId ?? ''),
    productName: String(raw.productName ?? ''),
    productSlug: String(raw.productSlug ?? ''),
    author: String(raw.author ?? ''),
    rating: Number(raw.rating) || 0,
    title: String(raw.title ?? ''),
    body: String(raw.body ?? ''),
    isVerifiedBuyer: Boolean(raw.isVerifiedBuyer),
    isApproved: Boolean(raw.isApproved),
    userPhone: raw.userPhone ? String(raw.userPhone) : undefined,
    createdAt: String(raw.createdAt ?? ''),
  };
}

function mapQuestion(raw: Record<string, unknown>): QuestionRow {
  return {
    id: String(raw.id ?? ''),
    productId: String(raw.productId ?? ''),
    productName: String(raw.productName ?? ''),
    productSlug: String(raw.productSlug ?? ''),
    author: String(raw.author ?? ''),
    body: String(raw.body ?? ''),
    answersCount: Number(raw.answersCount) || 0,
    pendingAnswers: Number(raw.pendingAnswers) || 0,
    isApproved: Boolean(raw.isApproved),
    userPhone: raw.userPhone ? String(raw.userPhone) : undefined,
    createdAt: String(raw.createdAt ?? ''),
  };
}

function mapAnswer(raw: Record<string, unknown>): AnswerRow {
  return {
    id: String(raw.id ?? ''),
    questionId: String(raw.questionId ?? ''),
    productId: String(raw.productId ?? ''),
    productName: String(raw.productName ?? ''),
    productSlug: String(raw.productSlug ?? ''),
    questionAuthor: String(raw.questionAuthor ?? ''),
    questionBody: String(raw.questionBody ?? ''),
    author: String(raw.author ?? ''),
    body: String(raw.body ?? ''),
    isAdmin: Boolean(raw.isAdmin),
    isVerifiedBuyer: Boolean(raw.isVerifiedBuyer),
    isAnonymous: Boolean(raw.isAnonymous),
    isApproved: Boolean(raw.isApproved),
    userPhone: raw.userPhone ? String(raw.userPhone) : undefined,
    createdAt: String(raw.createdAt ?? ''),
  };
}

export const reviewService = {
  async listReviews(params: { page?: number; limit?: number; status?: ModerationStatus } = {}): Promise<PageResult<ReviewRow>> {
    const res = await apiClient.get<PageResult<Record<string, unknown>>>('/admin/reviews', { params });
    const page = unwrapData(res, 'خطا در دریافت نظرات');
    return {
      ...page,
      data: (page.data ?? []).map(mapReview),
    };
  },
  async approveReview(id: string) {
    const res = await apiClient.post(`/admin/reviews/${id}/approve`);
    unwrapData(res, 'خطا در تأیید نظر');
  },
  async rejectReview(id: string) {
    const res = await apiClient.delete(`/admin/reviews/${id}`);
    unwrapData(res, 'خطا در حذف نظر');
  },
  async listQuestions(params: { page?: number; limit?: number; status?: ModerationStatus } = {}): Promise<PageResult<QuestionRow>> {
    const res = await apiClient.get<PageResult<Record<string, unknown>>>('/admin/questions', { params });
    const page = unwrapData(res, 'خطا در دریافت پرسش‌ها');
    return {
      ...page,
      data: (page.data ?? []).map(mapQuestion),
    };
  },
  async approveQuestion(id: string) {
    const res = await apiClient.post(`/admin/questions/${id}/approve`, {});
    unwrapData(res, 'خطا در تأیید پرسش');
  },
  async createAdminAnswer(id: string, body: string) {
    const res = await apiClient.post(`/admin/questions/${id}/answers`, { body });
    unwrapData(res, 'خطا در ثبت پاسخ');
  },
  /** @deprecated use createAdminAnswer */
  async answerQuestion(id: string, answer: string) {
    return this.createAdminAnswer(id, answer);
  },
  async rejectQuestion(id: string) {
    const res = await apiClient.delete(`/admin/questions/${id}`);
    unwrapData(res, 'خطا در حذف پرسش');
  },
  async listAnswers(params: { page?: number; limit?: number; status?: ModerationStatus } = {}): Promise<PageResult<AnswerRow>> {
    const res = await apiClient.get<PageResult<Record<string, unknown>>>('/admin/question-answers', { params });
    const page = unwrapData(res, 'خطا در دریافت پاسخ‌ها');
    return {
      ...page,
      data: (page.data ?? []).map(mapAnswer),
    };
  },
  async approveAnswer(id: string) {
    const res = await apiClient.post(`/admin/question-answers/${id}/approve`);
    unwrapData(res, 'خطا در تأیید پاسخ');
  },
  async rejectAnswer(id: string) {
    const res = await apiClient.delete(`/admin/question-answers/${id}`);
    unwrapData(res, 'خطا در حذف پاسخ');
  },
};
